import TimelineIcon from "@/assets/icons/ui/timeline.svg";
import { ArtifactMarkdown } from "@/component/ArtifactMarkdown";
import { ExpandableContainer } from "@/component/ExpandableContainer";
import { ExpandableIconButton } from "@/component/ExpandableIconButton";
import { ArtifactsViewerData } from "@/hooks/useArtifactsViewerData";
import { GenerateType, Scene, Shot } from "@/types/artifact";
import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import classNames from "classnames";
import React from "react";
import { useArtifactsViewerContext } from "../../context/ArtifactsViewerContext";
import { Section } from "../Section";
import styles from "./index.module.css";

type ShotsTableProps = ArtifactsViewerData["shots"];

interface SceneTableData extends Scene {
  key: string;
  sceneNumber: string;
}
interface ShotTableData extends Shot {
  key: string;
  shotNumber: string;
}

export const ShotsTable = React.memo(function ShotsTable(
  props: ShotsTableProps
) {
  const { id, data, visible, title } = props;
  const { onSwitchToTimeline } = useArtifactsViewerContext();

  const { shots, scenes } = data;

  if (!visible) {
    return null;
  }

  const handleTimelineViewClick = () => {
    onSwitchToTimeline?.("shot");
  };

  const sceneColumns: ColumnsType<SceneTableData> = [
    {
      title: <div className="h-[26px] leading-[26px]">Scene#</div>,
      dataIndex: "sceneNumber",
      key: "sceneNumber",
      width: 100,
      align: "center",
      render: (text: string) => <span className="font-mono">{text}</span>,
    },
    {
      title: "Caption",
      dataIndex: "caption",
      key: "caption",
      render: (caption: string) => {
        if (!caption) return null;
        return (
          <ArtifactMarkdown
            className={classNames(styles.caption, "text-text-secondary")}
          >
            {caption.replaceAll("[", "\\[").replaceAll("]", "\\]")}
          </ArtifactMarkdown>
        );
      },
    },
  ];

  const sceneDataSource: SceneTableData[] = scenes.map((scene, index) => ({
    ...scene,
    key: `scene-${index}`,
    sceneNumber: String(scene.scene_index + 1),
  }));

  const shotColumns: ColumnsType<ShotTableData> = [
    {
      title: <div className="h-[26px] leading-[26px]">Scene#</div>,
      dataIndex: "sceneIndex",
      key: "sceneNumber",
      width: 100,
      align: "center",
      render: (text: string) => <span className="font-mono">{text}</span>,
    },
    {
      title: <div className="h-[26px] leading-[26px]">Shot#</div>,
      dataIndex: "shotNumber",
      key: "shotNumber",
      width: 100,
      align: "center",
      render: (text: string) => <span className="font-mono">{text}</span>,
    },
    {
      title: "Caption",
      dataIndex: "caption",
      key: "caption",
      render: (caption: string) => {
        if (!caption) return null;
        return (
          <ArtifactMarkdown
            className={classNames(styles.caption, "text-text-secondary")}
          >
            {caption.replaceAll("[", "\\[").replaceAll("]", "\\]")}
          </ArtifactMarkdown>
        );
      },
    },
    {
      title: "Shot Type",
      dataIndex: "generate_type",
      key: "generate_type",
      width: 120,
      align: "center",
      render: (generate_type: GenerateType) =>
        generate_type
          ? generate_type.charAt(0).toUpperCase() + generate_type.slice(1)
          : "",
    },
    {
      title: "Duration (s)",
      dataIndex: "duration",
      key: "duration",
      width: 120,
      align: "center",
      render: (duration: number) => duration.toFixed(1),
    },
  ];

  const shotDataSource: ShotTableData[] = shots.map((shot, index) => ({
    ...shot,
    key: `shot-${index}`,
    shotNumber: String(shot.shot_index + 1),
    sceneIndex: String(shot.scene_index + 1),
  }));

  return (
    <Section
      id={id}
      title={title}
      extra={
        <ExpandableIconButton
          className="ml-1 align-text-bottom"
          icon={<TimelineIcon className="w-6 h-6" />}
          onClick={handleTimelineViewClick}
        >
          View In Timeline
        </ExpandableIconButton>
      }
    >
      {scenes.length > 0 && (
        <ExpandableContainer maxHeight={400} maskHeight={100}>
          <Table
            bordered={false}
            columns={sceneColumns}
            dataSource={sceneDataSource}
            pagination={false}
            size="middle"
            rowHoverable={false}
          />
        </ExpandableContainer>
      )}

      {shots.length > 0 && (
        <div className="mt-8">
          <ExpandableContainer maxHeight={400} maskHeight={100}>
            <Table
              bordered={false}
              columns={shotColumns}
              dataSource={shotDataSource}
              pagination={false}
              size="middle"
              rowHoverable={false}
            />
          </ExpandableContainer>
        </div>
      )}
    </Section>
  );
});
